import React, { useRef, useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap, useMapEvent } from 'react-leaflet';
import { NavLink, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import 'leaflet/dist/leaflet.css';
import L, { LatLngBoundsLiteral } from 'leaflet';
import { ISystem } from '../../helpers/interfaces';
import { L0, L1, L2, L3, L4 } from '../../assets/jss/wisene-material-dashboard-pro-react-types';
import { renderToStaticMarkup } from "react-dom/server";
import Room from '@material-ui/icons/Room';
import ErrorText from '../ErrorComponent/ErrorText';
import './SystemGroupMap.css';

interface SystemGroupMapProps {
    systems: ISystem[];
    groupId: number;
    systemsCount: number;
    mobileView: boolean;
}

const SystemGroupMap = ({
    systems,
    groupId,
    systemsCount,
    mobileView
}: SystemGroupMapProps) => {
    const mapRef = useRef(null);
    const { id } = useParams();
    const [scrollWheelZoom, setScrollWheelZoom] = useState(false);

    const ScrollWheelZoomController = ({ enabled }) => {
        const map = useMap();

        useEffect(() => {
            if (!map) return;
            enabled ? map.scrollWheelZoom.enable() : map.scrollWheelZoom.disable();
        }, [enabled, map]);

        return null;
    };

    const calculateBounds = (): LatLngBoundsLiteral | null => {
        const systemCoordinates = systems
            .filter((sys) => sys.latitude?.Valid && sys.longitude?.Valid)
            .map((sys) => [sys.latitude.Float64, sys.longitude.Float64]);

        if (systemCoordinates.length > 0) {
            return systemCoordinates as LatLngBoundsLiteral;
        }
        return null;
    };

    const customIcon = (system: ISystem) => {
    const iconMarkup = renderToStaticMarkup(<Room fontSize='large' />);
    const colorMap: Record<string, string> = {
        none: L0,
        unknown_or_NA: L0,
        L1: L1,
        L2: L2,
        L3: L3,
        L4: L4
    };
    const color = colorMap[system.NodesHighestThreatLevelActiveNonobstructed] ?? L0;

    return L.divIcon({
        className: 'custom-icon',
        iconSize: [50, 50],
        html: `<div style="color: ${color}; width: 30px; height: 30px; border-radius: 50%;">${iconMarkup}</div>`
    });
    };

  if (!groupId || String(groupId) !== id || !systems || (systems?.length && systems.length !== systemsCount)) {
    return null;
  }

  try {
    const bounds = calculateBounds();

    const MapEvents = () => {
      const map = useMap();
      useMapEvent('click', () => setScrollWheelZoom((prev) => !prev));
      useMapEvent('blur', () => setScrollWheelZoom(false));

      useEffect(() => {
        if (!map || !bounds) return;

        const t = window.setTimeout(() => {
          try {
            if ((map as any).invalidateSize) (map as any).invalidateSize(true);
            map.fitBounds(bounds as any, { padding: [15, 15] });
          } catch (e) {
            console.debug('fitBounds failed', e);
          }
        }, 0);

        return () => clearTimeout(t);
      }, [map, bounds]);

      return null;
    };
    
    return (
      <MapContainer
        ref={mapRef}
        key={groupId} 
        boundsOptions={{ padding: [15, 15] }}
        zoom={13}
        scrollWheelZoom={scrollWheelZoom}
        style={{
          width: 'calc(100% - 30px)',
          height: '500px',
          marginLeft: '15px',
          marginRight: '15px',
          borderRadius: '5px',
          boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.14)'
        }}
      >
        <MapEvents />
        <ScrollWheelZoomController enabled={scrollWheelZoom} />

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap contributors'
        />

        {systems?.map((system, idx) => (
          <Marker
            key={idx}
            position={[system.latitude.Float64, system.longitude.Float64]}
            icon={customIcon(system)}
          >
            <Popup>
              <NavLink to={`/systems/${system.ID}/dashboard`}>
                <div>{system.name}</div>
                <div>
                  Max deflection: {system.nodesHighestDeflection}%{' '}
                  {system.NodesHighestThreatLevelActiveNonobstructed === 'none' ||
                    system.NodesHighestThreatLevelActiveNonobstructed === 'unknown_or_NA'
                    ? null
                    : `(${system.NodesHighestThreatLevelActiveNonobstructed})`}
                </div>
              </NavLink>
            </Popup>

            {!mobileView && <Tooltip>{system.name}</Tooltip>}
          </Marker>
        ))}
      </MapContainer>
    );
  } catch (error) {
    console.debug('ERROR_DEBUG', error);
    return <ErrorText message="Corrupted map data.." />;
  }
};

const mapStateToProps = (state) => ({
  mobileView: state.dashboard.mobileView
});

export default connect(mapStateToProps)(SystemGroupMap);
